import { createMachine, assign, DoneInvokeEvent, MachineConfig, sendParent } from "xstate";
import { v4 as uuid } from "uuid";
import { Permission } from "@iventis/domain-model/model/permission";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { Node } from "@iventis/domain-model/model/node";
import { TwoWayObject, permissionSquasher } from "@iventis/utilities";
import { choose } from "xstate/lib/actions";
import { libraryApi } from "@iventis/api/src/api";
import { translate } from "@iventis/translations/translation";
import { Content } from "@iventis/translations";
import { toast } from "@iventis/toasts/src/toast";
import { diffPermissions, getPermissions, libraryPermissionBaseURL, restoreInheritance, breakInheritance, libraryPermissionLockUrl, canLock } from "./permission-utilitites";

export type PermissionContext = {
    originalPermissions: Permission[];
    latestPermissions: Permission[];
    localToRemoteMapping: TwoWayObject<string, string>;
    permissionSelector: string;
    errorMessage: string;
    node: Node;
    close: boolean;
    isChild: boolean;
    canLock: boolean; // Has the user got the authorisation to lock/unlock resources. This is calculated different to normal authorisations.
    // 'IS NODE CREATED' IS ONLY SUPPORTED WHEN ADMIN IS REQUIRED TO CREATE THE ENTITY.
    // TODO: SUPPORT 'IS NODE CREATED' WHEN PERMISSION BELOW ADMIN IS REQUIRED TO CREATE THE ENTITY.
    isNodeCreated: boolean; // Node may not be fully created yet in the nodes service. In this case, use parent Id to get permissions. Speeds up permission screen loading time.
};

const initialPermissionContext = {
    originalPermissions: null,
    latestPermissions: null,
    localToRemoteMapping: new TwoWayObject<string, string>({}, {}),
    permissionSelector: null,
    errorMessage: null,
    node: null,
    close: false,
    isChild: false,
    canLock: false,
    isNodeCreated: true,
    userId: null,
};

type AddPermissionEvent = {
    type: "ADD_PERMISSION";
    payload: Permission;
};
type RemovePermissionEvent = {
    type: "REMOVE_PERMISSION";
    payload: Permission;
};
type ChangePermissionEvent = {
    type: "CHANGE_PERMISSION";
    payload: { changingPermission: Permission; authorisation: Authorisation };
};
type OpenPermissionSelectorEvent = {
    type: "OPEN_PERMISSION_SELECTOR";
    payload: string;
};
type BreakInheritanceEvent = {
    type: "BREAK_INHERITANCE";
};

type RestoreInheritanceEvent = {
    type: "RESTORE_INHERITANCE";
};

type SaveEvent = {
    type: "SAVE";
};

type CloseEvent = {
    type: "CLOSE";
};
type RetryEvent = {
    type: "RETRY";
};

export type PermissionEvents =
    | AddPermissionEvent
    | RemovePermissionEvent
    | ChangePermissionEvent
    | OpenPermissionSelectorEvent
    | SaveEvent
    | CloseEvent
    | RetryEvent
    | BreakInheritanceEvent
    | RestoreInheritanceEvent;

// Add, update, remove permissions
const actions = {
    addPermissions: assign({
        latestPermissions: (context: PermissionContext, event: AddPermissionEvent) => {
            const localId = uuid();
            context.localToRemoteMapping.createKey(localId);
            return [...context.latestPermissions, { ...event.payload, id: localId }];
        },
    }),
    removePermissions: assign({
        latestPermissions: (context: PermissionContext, event: RemovePermissionEvent) => context.latestPermissions.filter((permission) => permission.id !== event.payload.id),
    }),
    changePemrissions: assign({
        latestPermissions: (context: PermissionContext, event: ChangePermissionEvent) => {
            const newPermission: Permission = { ...event.payload.changingPermission, authorisation: event.payload.authorisation };
            // Replace the permission in our permissions list
            const indexChangedPermission = context.latestPermissions.findIndex((existingPermission) => existingPermission.id === event.payload.changingPermission.id);
            return context.latestPermissions.map((existingPermission, index) => (index === indexChangedPermission ? newPermission : existingPermission));
        },
        permissionSelector: () => null,
    }),
    setInitialPermissions: assign((context: PermissionContext, event: DoneInvokeEvent<{ permissions: Permission[]; canLock: boolean }>) => {
        event.data.permissions.forEach(({ id }) => context.localToRemoteMapping.create(id, id));
        const squashedPermissions = permissionSquasher(event.data.permissions, context.node.sourceId);
        return { ...context, originalPermissions: squashedPermissions, latestPermissions: squashedPermissions, canLock: event.data.canLock };
    }),
    sendParentContext: sendParent((context) => ({ type: "UPDATE_PERMISSIONS", payload: context })),
    sendParentIdleState: sendParent(() => ({ type: "UPDATE_PERMISSIONS_IDLE_STATE", payload: true })),
    sendParentNonIdleState: sendParent(() => ({ type: "UPDATE_PERMISSIONS_IDLE_STATE", payload: false })),
};

// If instance is a child machine, send parent latest context
const isChild = (context: PermissionContext) => context.isChild;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const permissionMachineConfig: MachineConfig<PermissionContext, any, PermissionEvents> = {
    id: "permissions",
    initial: "loading",
    context: initialPermissionContext,
    states: {
        loading: {
            invoke: {
                src: "getPermissions",
                onDone: [
                    { cond: isChild, target: "idle", actions: [actions.setInitialPermissions, actions.sendParentContext] },
                    { target: "idle", actions: [actions.setInitialPermissions] },
                ],
                onError: "getPermissionsFailed",
            },
        },
        getPermissionsFailed: {
            on: { RETRY: "loading" },
        },
        idle: {
            id: "idle",
            entry: choose([{ cond: isChild, actions: actions.sendParentIdleState }]),
            exit: choose([{ cond: isChild, actions: actions.sendParentNonIdleState }]),
            on: {
                ADD_PERMISSION: [{ cond: isChild, actions: [actions.addPermissions, actions.sendParentContext] }, { actions: [actions.addPermissions] }],
                REMOVE_PERMISSION: [{ cond: isChild, actions: [actions.removePermissions, actions.sendParentContext] }, { actions: [actions.removePermissions] }],
                CHANGE_PERMISSION: [{ cond: isChild, actions: [actions.changePemrissions, actions.sendParentContext] }, { actions: [actions.changePemrissions] }],
                OPEN_PERMISSION_SELECTOR: {
                    actions: assign({ permissionSelector: (_, event) => event.payload }),
                },
                SAVE: "saving",
                CLOSE: "finished",
                BREAK_INHERITANCE: "breakInheritance",
                RESTORE_INHERITANCE: "restoreInheritance",
            },
        },
        breakInheritance: {
            id: "breakInheritance",
            invoke: {
                src: "breakInheritance",
                onDone: {
                    target: "#idle",
                    actions: assign((context, event: DoneInvokeEvent<Permission[]>) => {
                        event.data.forEach(({ id }) => context.localToRemoteMapping.create(id, id));
                        const squashedPermissions = permissionSquasher(event.data, context.node.sourceId);
                        return { ...context, originalPermissions: squashedPermissions, latestPermissions: squashedPermissions };
                    }),
                },
                onError: "#idle",
            },
        },
        restoreInheritance: {
            invoke: {
                src: "restoreInheritance",
                onDone: {
                    target: "#idle",
                    actions: assign((context, event: DoneInvokeEvent<Permission[]>) => {
                        event.data.forEach(({ id }) => context.localToRemoteMapping.create(id, id));
                        const squashedPermissions = permissionSquasher(event.data, context.node.sourceId);
                        return { ...context, originalPermissions: squashedPermissions, latestPermissions: squashedPermissions };
                    }),
                },
                onError: "#idle",
            },
        },
        saving: {
            invoke: {
                src: "save",
                onDone: [
                    { cond: (context) => context.close, target: "finished", actions: assign({ originalPermissions: (context) => context.latestPermissions }) },
                    { target: "idle", actions: assign({ originalPermissions: (context) => context.latestPermissions }) },
                ],
                onError: "idle",
            },
            on: {
                CLOSE: {
                    actions: assign((context) => ({ ...context, close: true })),
                },
            },
        },
        finished: {
            type: "final",
        },
    },
    on: {
        CLOSE: { actions: assign((context) => ({ ...context, close: true })) },
    },
    predictableActionArguments: true,
};

const permissionServices = {
    save: async (context) => {
        // Statelessly calculate the difference between the old and the new permissions
        const { create: permissionsToCreate, delete: permissionsToDelete, update: permissionsToUpdate } = diffPermissions(context.originalPermissions, context.latestPermissions);
        try {
            await Promise.all([
                // Create necessary permissions
                ...permissionsToCreate
                    .filter((p) => p.authorisation !== Authorisation.Lock)
                    .map((permission) =>
                        libraryApi.post<Permission>(libraryPermissionBaseURL, permission).then((response) => {
                            // Pair our local ID with the new remote ID
                            context.localToRemoteMapping.create(permission.id, response.data.id);
                            return response;
                        })
                    ),
                // Update necessary permissions
                ...permissionsToUpdate.filter((p) => p.authorisation !== Authorisation.Lock).map((permission) => libraryApi.put(libraryPermissionBaseURL, permission)),
                // Remove necessary permissions
                ...permissionsToDelete.filter((p) => p.authorisation !== Authorisation.Lock).map((permission) => libraryApi.delete(libraryPermissionBaseURL + permission.id)),
                // Create any new permission locks
                ...permissionsToCreate
                    .filter((p) => p.authorisation === Authorisation.Lock)
                    .map((permission) =>
                        libraryApi.post<Permission>(libraryPermissionLockUrl + permission.resourceId).then((response) => {
                            // Pair our local ID with the new remote ID
                            context.localToRemoteMapping.create(permission.id, response.data.id);
                            return response;
                        })
                    ),
                // Remove any permission lock
                ...permissionsToDelete
                    .filter((p) => p.authorisation === Authorisation.Lock)
                    .map((permission) => libraryApi.delete(libraryPermissionLockUrl + permission.resourceId)),
                // Do not need to consider updating lock permissions as they cannot be updated. A lock either exists or it does not.
            ]);
        } catch (error) {
            toast.error({
                title: translate(Content.errors.custom_network_error.update.title, { entity: translate(Content.permissions.permissions) }),
                message: translate(Content.errors.custom_network_error.update.message, { entity: translate(Content.permissions.permissions) }),
            });
            throw error;
        }
    },
    getPermissions: async ({ node, isNodeCreated }) => {
        const permissions = await getPermissions(node, isNodeCreated);
        const canUserLock = isNodeCreated ? await canLock(node.id) : true;
        return { permissions, canLock: canUserLock };
    },
    restoreInheritance: ({ node }) => restoreInheritance(node.sourceId),
    breakInheritance: ({ node }) => breakInheritance(node.sourceId),
};

export const permissionsEditMachine = createMachine<PermissionContext, PermissionEvents>(permissionMachineConfig, {
    services: permissionServices,
});
